import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { prettyPlayerId } from 'app/core/utilities/user-util';
import { HeaderToastMessageService } from 'app/modules/social/services/header-toast-message.service';
import { AuthService } from 'app/services/auth.service';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { isNil, notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import {
  EmbeddedMedia,
  EndMirrorMessage,
  IncomingMessage,
  IncomingMessageType,
  MirrorState,
  MirrorType,
  OutgoingMessageType,
  Presenter,
  PresenterType,
  ScreenShareStatus,
  SocialEventType,
  StartMirrorMessage,
  UpdateType,
} from 'messages/websocket.messages';
import { PresentationSettings } from 'messages/websocket.messages';

import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { MAX_PRESENTERS } from '../../social-state-machine/social-state.constants';
import { CrossRoomPresentationStatus, socialAppActions, socialAppSelectors } from '../../state/app';
import { PresenterVideoQuality } from '../../types/media';
import { LivestreamService } from '../livestream/livestream.service';
import { MultimediaUserService } from '../multimedia-user.service';
import { PerformerService } from '../performer.service';
import { ShareScreenService } from '../share-screen/share-screen.service';
import { SubscriptionReason, SubscriptionType } from '../subscription/subscription.types';
import { LoadingTransitionLabel, TransitionService } from '../transition.service';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { TransitionPresentationService } from './../transition-presentation.service';
import { MirrorSubscriber } from './mirror-subscriber';
import { PresentationAudioService } from './presentation-audio.service';
import { PresentationRecorderService } from './presentation-recorder.service';
import { createMirrorId } from './util';

interface MirrorMedia {
  audio?: MediaStream;
  audioId: string;
  video?: MediaStream;
  videoId: string;
}

export type MirrorMediaStreams = Record<string, MirrorMedia>;

/**
 * Service for managing presentation control
 *
 * Initiating presentation is a state modification action. This service controls that state modification for
 * presentations and manages cross-room presentation subscriptions
 */
@Injectable({
  providedIn: 'root',
})
export class PresentationService extends Loggable {
  private authService = inject(AuthService);
  private websocketService = inject(WebsocketService);
  private videoRoomService = inject(VideoRoomService);
  private shareScreenService = inject(ShareScreenService);
  private toastMessageService = inject(HeaderToastMessageService);
  private livestreamService = inject(LivestreamService);
  private presentationAudioService = inject(PresentationAudioService);
  private presentationRecorderService = inject(PresentationRecorderService);
  private transitionService = inject(TransitionService);
  private transitionPresentationService = inject(TransitionPresentationService);
  private store = inject<Store<AppState>>(Store);
  private performerService = inject(PerformerService);
  private multimediaUserService = inject(MultimediaUserService);

  private isCrossRoomPresentation = false;
  private isGuest = false;
  public isPresentationActive = false;
  private isPresentationAudioMixEnabled = false;
  private isScreenShareActive = false;
  private mirrorId?: string;
  private mirrorSocialId?: string;
  private mirrorMediaStreams: MirrorMediaStreams = {};
  private mirrorMediaStreamSource = new BehaviorSubject<MirrorMediaStreams>({});
  private mirrorSubscribers = new Map<string, MirrorSubscriber>();
  private presenterIds: string[] = [];
  private presenters: Record<string, Presenter> = {};
  private requestedPresenterIds: string[] = [];
  private socialId?: string;
  private user?: User;
  private userId = 'unknown';
  private hd_presenter_1080p = false;
  private mirrors: MirrorState[] = [];
  private reason = SubscriptionReason.PRESENTER;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isWideRecording = false;

  public isWaitingPresentation = false;
  public mirrorMediaStreams$ = this.mirrorMediaStreamSource.asObservable();
  public isConnectedRoom = false;
  public speakingParticipants: User[] = [];

  private presentationV3 = false;

  public constructor() {
    super();
    this.setDisplayName('PresentationService');

    // User state
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
        this.userId = user?.userId ?? 'unknown';
        this.isGuest = this.authService.isAnonymous();
      });

    // Social state
    this.store.select(socialAppSelectors.getCurrentSocialId).subscribe((socialId) => (this.socialId = socialId));

    // When the joined social changes, and the user was joined to one previously, clear any existing mirror peers
    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(
        pairwise(),
        filter(([prev, next]) => prev !== next && !isNil(prev)),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.destroyMirrorSubscribers());

    // Presentation state
    this.store.select(socialAppSelectors.getPresenterIds).subscribe((ids) => {
      this.presenterIds = ids;

      if (this.mirrors.length) {
        this.mirrors.forEach((m) => {
          const mirror = this.mirrorSubscribers.get(m.mirrorId);
          const id = m.mirrorId.split(':');
          if (this.presenterIds.includes(id[0]) || m.mirrorId.includes('livestream')) {
            if (!mirror) {
              const { audio, mirrorId, publishPeerTime, video } = m;
              const subscriber = new MirrorSubscriber(
                this.videoRoomService,
                this.websocketService,
                this.store,
                mirrorId,
                publishPeerTime,
                audio,
                video,
                this.reason,
                this.onMediaStreamChange,
              );
              this.mirrorSubscribers.set(mirrorId, subscriber);
            }
          }
        });
      }
    });

    this.store
      .select(socialAppSelectors.getRequestedPresenterIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ids) => (this.requestedPresenterIds = ids));

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation) => {
        this.isPresentationActive = !!presentation;
        this.presenters = presentation?.presenters ?? {};
        window.dispatchEvent(new Event('resize'));
      });

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoomPresentation) => (this.isCrossRoomPresentation = isCrossRoomPresentation));

    // Initiate mirror when this user is cross-room presenting and a mirror session is not already active.
    // This initiates mirrors for non-initiating presenters.
    this.store
      .select(socialAppSelectors.isUserCrossRoomPresenter)
      .pipe(
        filter((isCrossRoom) => isNil(this.mirrorId) && isCrossRoom),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.startMirror(MirrorType.PUBLISHER);
      });

    // End mirror when this presenter is no longer presenting
    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(
        filter((isPresenting) => notNil(this.mirrorId) && !isPresenting),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.endMirror();
      });

    // End mirror when presentation changes from cross-room to local and user is still presenting
    this.store
      .select(socialAppSelectors.getCrossRoomPresentationStatus)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevCrossRoomStatus, crossRoomStatus]) => {
        if (prevCrossRoomStatus.isCrossRoom && !crossRoomStatus.isCrossRoom && crossRoomStatus.isPresenting) {
          this.endMirror();
        }
        const message = this.getNotificationMessage(prevCrossRoomStatus, crossRoomStatus);
        if (notNil(message)) {
          this.toastMessageService.pushTemporaryMessage({ message });
        }
      });

    // Destroy mirror subscribers when cross room presentations end
    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previousIsCrossRoom, isCrossRoom]) => {
        if (previousIsCrossRoom && !isCrossRoom) {
          this.destroyMirrorSubscribers();
        }
      });

    combineLatest([
      this.store.select(socialAppSelectors.isConnectedRoom),
      this.store.select(socialAppSelectors.getCurrentConnectedRoomSet),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isConnectedRoom, currentConnectedRoomCounts]) => {
        this.isConnectedRoom =
          isConnectedRoom && !!currentConnectedRoomCounts?.socials && currentConnectedRoomCounts?.socials?.length > 1;
      });

    combineLatest([
      this.store.select(socialAppSelectors.getSpeakingParticipantValues),
      this.store.select(socialAppSelectors.isSinglePushToTalkActive),
    ])
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(([speakingParticipants, isSinglePushToTalkActive]) => {
        if (isSinglePushToTalkActive) {
          this.speakingParticipants = speakingParticipants.length ? [speakingParticipants[0]] : [];
          return;
        }
        this.speakingParticipants = speakingParticipants;
      });

    // Screen share state
    this.store
      .select(socialAppSelectors.isScreenShareActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isScreenShareActive) => (this.isScreenShareActive = isScreenShareActive));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_audio_mix)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_audio_mix) => (this.isPresentationAudioMixEnabled = presentation_audio_mix));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.hd_presenter_1080p)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hd_presenter_1080p) => (this.hd_presenter_1080p = hd_presenter_1080p));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3 = presentation_v3;
      });

    this.websocketService.message$
      .pipe(
        switchMap((msg) => this.handleMessage(msg)),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
      });

    combineLatest([
      this.store.select(socialAppSelectors.getScreenShareState),
      this.store.select(socialAppSelectors.isCrossRoomPresentation),
    ])
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([[oldScreenShareState], [newScreenShareState, isCrossRoomPresentation]]) => {
        // If after a cross-room screenshare is ended remove from the state the mirrorId
        if (
          isCrossRoomPresentation &&
          oldScreenShareState.status === ScreenShareStatus.ACTIVE &&
          newScreenShareState.status === ScreenShareStatus.NONE
        ) {
          const mirrors = this.mirrorSubscribers.keys();
          for (const mirrorId of mirrors) {
            const isScreenShareMirror = mirrorId.split(':')[1] === MirrorType.SCREEN_SHARE;
            if (isScreenShareMirror) {
              const subscriber = this.mirrorSubscribers.get(mirrorId);
              subscriber?.destroy();
              this.mirrorSubscribers.delete(mirrorId);
            }
          }
        }
      });
  }

  private getNotificationMessage(
    prevCrossRoomStatus: CrossRoomPresentationStatus,
    crossRoomStatus: CrossRoomPresentationStatus,
  ): string | undefined {
    const { isPresenting } = crossRoomStatus;
    // Presentation went from local to cross-room
    if (!prevCrossRoomStatus.isCrossRoom && crossRoomStatus.isCrossRoom) {
      return isPresenting
        ? 'You are presenting to all rooms'
        : prevCrossRoomStatus.restrictedEvent === SocialEventType.NONE
          ? 'This presentation is happening across all rooms'
          : 'An event has ended because there is a presentation happening across all rooms';
    }

    // Presentation went from cross-room to local
    if (prevCrossRoomStatus.isCrossRoom && !crossRoomStatus.isCrossRoom) {
      return isPresenting
        ? 'You are presenting to only the current room'
        : crossRoomStatus.presentation
          ? 'This presentation is happening in the current room'
          : undefined;
    }
  }

  // Destroy any existing mirror subscribers
  private destroyMirrorSubscribers(): void {
    this.info('Destroying presentation mirror subscribers');
    this.mirrorSubscribers.forEach((subscriber) => subscriber.destroy());
    this.mirrorSubscribers.clear();
    this.mirrors = [];
  }

  /**
   *  Remove subscriptions from a mirror subscriber
   * @param presenter The presenter to remove
   */
  private removeMirror(mirrorId: string): void {
    this.info('Removing mirror', { mirrorId });
    const subscriber = this.mirrorSubscribers.get(mirrorId);
    subscriber?.destroy();
    this.mirrorSubscribers.delete(mirrorId);
    this.store.dispatch(socialAppActions.REMOVE_PARTICIPANT({ userId: mirrorId }));
  }

  /**
   * Deallocate presentation resources
   */
  public destroy(): void {
    this.info('Destroying presentation service');
    this.endMirror();
    this.destroyMirrorSubscribers();
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.mirrors = [];
    this.mirrorMediaStreamSource.next({});
    this.mirrorMediaStreamSource.complete();
    if (this.isPresentationAudioMixEnabled && this.isCrossRoomPresentation) {
      this.presentationAudioService.disableCrossRoomMix();
    }
  }

  /**
   * Initiate a presentation
   * @param selectedPresenters The users who are presenting
   * @param isCrossRoom Whether the presentation is cross-room or local to this room, defaults to true
   * @returns Promise<void>
   */
  public async startPresentation({
    activePresenters = {},
    embedCode,
    isCrossRoom = false,
    mediaEmbedType,
    selectedPresenters,
    settings,
    shouldTransition = true,
    updateType = UpdateType.CURRENT,
  }: {
    selectedPresenters: User[];
    isCrossRoom?: boolean;
    updateType?: UpdateType;
    activePresenters?: Record<string, Presenter>;
    settings?: PresentationSettings;
    shouldTransition?: boolean;
    mediaEmbedType?: MediaEmbedType;
    embedCode?: string;
  }): Promise<void> {
    if (!this.user) {
      this.error('Cannot start presentation, no user is set');
      return;
    }
    const { firstName, lastName } = this.user;
    const { isGuest, socialId, userId } = this;
    const requestCrossRoom = isCrossRoom && this.isConnectedRoom;
    const requestUpdateType = requestCrossRoom ? UpdateType.ALL : updateType;
    const userSelected = selectedPresenters.find((p) => userId === p.userId);
    const selectedRequestedPresenterIds = selectedPresenters
      .filter((p) => userId !== p.userId)
      .map((p) => p.userId)
      .filter(notNil);
    let media: EmbeddedMedia | undefined = undefined;
    switch (mediaEmbedType) {
      case MediaEmbedType.VIMEO:
        media = {
          url: `https://player.vimeo.com/video/${embedCode}`,
          playing: false,
          startTime: 0,
          playtingTime: 0,
        };
        break;
      // TODO: Add support for other media types
      // case MediaEmbedType.YOUTUBE:
      //   media = MediaEmbedType.YOUTUBE;
      //   break;
      default:
        media = undefined;
    }
    const presenters: Record<string, Presenter> = userSelected
      ? { [userId]: { userId, firstName, lastName, isGuest, socialId: socialId, media } }
      : activePresenters;

    if (shouldTransition) {
      let overlayText = '';

      if (requestCrossRoom) {
        overlayText = LoadingTransitionLabel.ALL;
      } else {
        overlayText = LoadingTransitionLabel.SINGLE;
      }

      // Starts loading effect

      if (!this.presentationV3) {
        this.transitionService.enableTransitionOverlay(overlayText, true).catch((err) => {
          this.error('Failed to execute transition', err);
        });
      } else {
        this.transitionPresentationService.executeTransitionToPresentation(overlayText).catch((err) => {
          this.error('Failed to execute transition', err);
        });
      }
    }

    await SocialStateMachine.shared.requestStartNewPresentation({
      userId,
      requestedPresenterIds: selectedRequestedPresenterIds,
      presenters,
      presentationCreator: userId,
      settings: settings,
      crossRoom: requestCrossRoom,
      updateType: requestUpdateType,
    });

    await SocialStateMachine.shared.requestAddActivePresentation({
      socialId: this.socialId ?? '',
    });

    // adjust PresenterVideoQuality
    let presenterVideoQuality: PresenterVideoQuality;
    if (this.isWideRecording) {
      presenterVideoQuality = PresenterVideoQuality.QUALITY_1080P;
    } else {
      presenterVideoQuality = this.hd_presenter_1080p
        ? PresenterVideoQuality.QUALITY_1080P
        : PresenterVideoQuality.QUALITY_720P;
    }
    this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: presenterVideoQuality }));

    // If livestream publish
    Object.values(activePresenters).forEach((presenter) => {
      if (presenter.presenterType === PresenterType.LIVE_STREAM) {
        const LSCrossRoom = updateType === UpdateType.OTHERS;
        this.livestreamService.addActiveLivestream(presenter, LSCrossRoom);
      }
    });

    // If we're start a cross-room this presentation (non-OTHERS update type), set the mix state to cross-room.
    // For updating presentations in-place, the presentation modification process will update the mix type
    if (this.isPresentationAudioMixEnabled && requestCrossRoom) {
      this.presentationAudioService.enableCrossRoomMix();
    }

    if (this.isWideRecording) {
      this.presentationRecorderService.handleRecordPresentation();
    }
  }

  /**
   * Change presentation from single to crossroom
   * @param selectedPresenters The users who are presenting
   * @returns Promise<void>
   */
  public async changePresentationSingleToCrossRoom({
    activePresenters = {},
    selectedPresenters,
    settings,
  }: {
    selectedPresenters: User[];
    activePresenters?: Record<string, Presenter>;
    settings?: PresentationSettings;
  }): Promise<void> {
    if (!this.user) {
      this.error('Cannot start presentation, no user is set');
      return;
    }
    const { firstName, lastName } = this.user;
    const { isGuest, socialId, userId } = this;
    const requestUpdateType = UpdateType.OTHERS;
    const userSelected = selectedPresenters.find((p) => userId === p.userId);
    const selectedRequestedPresenterIds = selectedPresenters
      .filter((p) => userId !== p.userId)
      .map((p) => p.userId)
      .filter(notNil);
    const presenters: Record<string, Presenter> = userSelected
      ? { [userId]: { userId, firstName, lastName, isGuest, socialId: socialId } }
      : activePresenters;

    await SocialStateMachine.shared.requestStartCrossPresentation({
      userId,
      requestedPresenterIds: selectedRequestedPresenterIds,
      presenters,
      presentationCreator: userId,
      settings: settings,
      crossRoom: true,
      updateType: requestUpdateType,
    });

    // adjust PresenterVideoQuality
    let presenterVideoQuality: PresenterVideoQuality;
    if (this.isWideRecording) {
      presenterVideoQuality = PresenterVideoQuality.QUALITY_1080P;
    } else {
      presenterVideoQuality = this.hd_presenter_1080p
        ? PresenterVideoQuality.QUALITY_1080P
        : PresenterVideoQuality.QUALITY_720P;
    }
    this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: presenterVideoQuality }));
  }

  /**
   * Change presentation from Crossroom to single
   * @param selectedPresenters The users who are presenting
   * @returns Promise<void>
   */

  public async changePresentationCrossToSingleRoom(): Promise<boolean> {
    if (!this.isPresentationActive) {
      this.error('endPresentation: Cannot end presentation, no presentation is active');
      return false;
    }

    await SocialStateMachine.shared
      .requestEndPresentation({
        userId: this.userId,
        updateType: UpdateType.OTHERS,
      })
      .catch((e) => {
        this.error('endPresentation: Failed to end cross room presentation', e);
        return false;
      });
    return true;
  }

  public async updatePresentationMediaState(mediaState: EmbeddedMedia | undefined): Promise<void> {
    const newPresenter = { ...this.presenters[this.userId], media: mediaState };
    const { userId } = this;
    await SocialStateMachine.shared.requestUpdatePresenter({
      presenter: newPresenter,
      userId,
      updateType: (this.isCrossRoomPresentation ? UpdateType.OTHERS : UpdateType.CURRENT) as UpdateType,
    });
  }

  public async updatePresentationMediaPlayState(mediaState: EmbeddedMedia): Promise<void> {
    const newPresenter = { ...this.presenters[this.userId], media: mediaState };
    const { userId } = this;
    await SocialStateMachine.shared.requestPlayMediaEmbed({
      playing: mediaState.playing,
      playtingTime: mediaState.playtingTime,
      presenter: newPresenter,
      userId,
      updateType: (this.isCrossRoomPresentation ? UpdateType.OTHERS : UpdateType.CURRENT) as UpdateType,
    });
  }

  /**
   * @param selectedPresenters Presenters selected in modal by the user
   * @param removedPresenters Current presenters removed in modal by user
   * @param updateType Update to whether the presentation is cross-room or local to this room
   */
  public async updatePresentation({
    media,
    removedPresenters,
    selectedPresenters,
    updateType,
  }: {
    updateType: UpdateType;
    removedPresenters: Record<string, boolean>;
    selectedPresenters: User[];
    media?: EmbeddedMedia;
  }): Promise<void> {
    if (!this.user) {
      this.error('Attempted to update presentation with no user');
      return;
    }
    const { firstName, lastName } = this.user;
    const { userId } = this;

    // Requested Presenters
    const selectedRequestedPresenterIds = selectedPresenters
      .map((p) => p.userId)
      .filter(notNil)
      .filter((presenterId) => presenterId !== userId);
    const currentUserSelected = selectedPresenters.find((p) => userId === p.userId);

    // Active Presenters
    const activePresenters: Presenter[] | undefined = currentUserSelected
      ? [
          {
            userId,
            firstName,
            lastName,
            isGuest: this.isGuest,
            socialId: this.socialId,
            media: media,
          },
        ]
      : undefined;

    // Removed Presenters
    const idsToRemove = Object.keys(removedPresenters).filter((id) => !!removedPresenters[id]);

    // Update the presentation state
    try {
      await SocialStateMachine.shared.requestUpdatePresenters({
        idsToRemove,
        activePresenters,
        presenterIdsToInvite: selectedRequestedPresenterIds,
        userId,
        updateType,
      });
    } catch (e) {
      this.error('Error request update presenters', e);
    }
  }

  /**
   * Updates the type of the presentation.
   *
   * @param crossRoom - Indicates if the presentation is local or cross-room
   */
  public async updatePresentationType({ crossRoom }: { crossRoom: boolean; updateType?: UpdateType }) {
    // local -> cross-room
    // Enable cross-room mixing when moving presentation from local to cross-room BEFORE changing the presentation
    if (this.isPresentationAudioMixEnabled && !this.isCrossRoomPresentation && crossRoom) {
      this.presentationAudioService.enableCrossRoomMix();
    }

    // cross-rom -> local
    // Remove cross-room mixing when moving presentation from cross-room to local AFTER changing the presentation
    if (this.isPresentationAudioMixEnabled && this.isCrossRoomPresentation && !crossRoom) {
      this.presentationAudioService.disableCrossRoomMix();
    }

    // Update Screen share
    if (this.isScreenShareActive) {
      this.shareScreenService.updateScreenShare(!!crossRoom).catch((e) => {
        this.error('Failed to update screen share', e);
      });
    }
    // Update current state for crossRoom
    await SocialStateMachine.shared.requestUpdatePresentation({
      updateType: UpdateType.CURRENT,
      userId: this.userId,
      updatedProperties: { crossRoom },
      operation: 'updatePresentationType',
    });

    // Unpublish cross-room presenters
    Object.values(this.presenters)
      .filter((p) => p.socialId !== this.socialId)
      .forEach(async (p) => {
        await this.removeActivePresenter(p.userId, UpdateType.CURRENT).catch((e) => this.error(e));
      });
  }

  /**
   * Start a mirror session
   */
  private startMirror(type: MirrorType): void {
    if (!this.user) {
      this.error('Attempted to start mirror with no publisher');
      return;
    }
    const mirrorId = createMirrorId(this.user, type);
    const socialId = this.getMirrorSocialId(this.user?.userId ?? '');

    this.info('Creating mirror session', { mirrorId, type });
    try {
      const msg: StartMirrorMessage = {
        t: OutgoingMessageType.START_MIRROR,
        mirrorId,
        type,
        socialId,
      };

      this.websocketService.sendMessage(msg);
    } catch (e) {
      const { message } = e as { message: string };
      this.error('Failed to start mirror session:', message);
    }

    this.mirrorId = mirrorId;
    this.mirrorSocialId = socialId;
  }

  /**
   * End active mirror sessions
   */
  private endMirror(): void {
    if (!this.mirrorId) {
      this.warn('Attempted to end mirror with no mirror id');
      return;
    }

    this.info('Ending mirror session', { mirrorId: this.mirrorId });
    const type = MirrorType.PUBLISHER;

    try {
      const msg: EndMirrorMessage = {
        t: OutgoingMessageType.END_MIRROR,
        mirrorId: this.mirrorId,
        type,
        socialId: this.mirrorSocialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      const { message } = e as { message: string };
      this.error('Failed to end mirror session:', message);
    }

    this.mirrorId = undefined;
    this.mirrorSocialId = undefined;
  }

  private getMirrorSocialId(userId: string): string {
    let socialId = '';
    if (this.presenters) {
      Object.values(this.presenters).map((p) => {
        if (p.userId === userId) {
          socialId = p.socialId ?? '';
        }
      });
    }
    return socialId;
  }

  private async handleMessage(msg: IncomingMessage): Promise<void> {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.INIT_STATE: {
        this.mirrors = msg.mirrors ?? [];
        msg.mirrors?.forEach((mirror) => {
          const m = this.mirrorSubscribers.get(mirror.mirrorId);
          const id = mirror.mirrorId.split(':');
          if (this.presenterIds.includes(id[0])) {
            if (!m) {
              const { audio, mirrorId, publishPeerTime, video } = mirror;
              const subscriber = new MirrorSubscriber(
                this.videoRoomService,
                this.websocketService,
                this.store,
                mirrorId,
                publishPeerTime,
                audio,
                video,
                this.reason,
                this.onMediaStreamChange,
              );
              this.mirrorSubscribers.set(mirrorId, subscriber);
            }
          }
        });
        break;
      }

      case IncomingMessageType.MIRROR_STARTED: {
        const { audio, mirrorId, mirrorPeerTime, video } = msg;
        const m = this.mirrorSubscribers.get(mirrorId);
        const id = mirrorId.split(':');
        if (this.presenterIds.includes(id[0])) {
          if (!m) {
            const subscriber = new MirrorSubscriber(
              this.videoRoomService,
              this.websocketService,
              this.store,
              mirrorId,
              mirrorPeerTime,
              audio,
              video,
              this.reason,
              this.onMediaStreamChange,
            );
            this.mirrorSubscribers.set(mirrorId, subscriber);
          }
        }
        break;
      }

      case IncomingMessageType.MIRROR_HUP: {
        const { mirrorId } = msg;
        const id = mirrorId.split(':');
        if (this.presenterIds.includes(id[0])) {
          this.removeMirror(mirrorId);
        }
        break;
      }

      case IncomingMessageType.MIRROR_OFFER: {
        const { d: offer, mirrorId, pt: peerTime } = msg;
        const mirrorSubscriber = this.mirrorSubscribers.get(mirrorId);
        const id = mirrorId.split(':');
        if (this.presenterIds.includes(id[0])) {
          if (!mirrorSubscriber) {
            this.warn('Received mirror offer message for unknown mirror', { mirrorId });
            return;
          }
          await mirrorSubscriber.addOffer(offer, peerTime);
        }
        break;
      }

      case IncomingMessageType.MIRROR_STATE: {
        const { mirrorId } = msg;
        const mirrorSubscriber = this.mirrorSubscribers.get(mirrorId);
        const id = mirrorId.split(':');
        if (this.presenterIds.includes(id[0])) {
          if (!mirrorSubscriber) {
            this.warn('Received mirror state message for unknown mirror', { mirrorId });
            return;
          }
          mirrorSubscriber.handleMirrorState(msg);
        }
        break;
      }

      case IncomingMessageType.STATE_NOT_CHANGED: {
        this.isWaitingPresentation = false;
        window.setTimeout(() => {
          if (this.presentationV3) {
            this.transitionPresentationService.enableFadeInBackground().catch((err) => {
              this.error('Failed to execute transition', err);
              this.toastMessageService.pushTemporaryMessage({
                message: 'There is a presentation in progress in another room!',
              });
            });
          } else {
            this.transitionService.enableFadeInBackground().catch((err) => {
              this.error('Failed to execute transition', err);
              this.toastMessageService.pushTemporaryMessage({
                message: 'There is a presentation in progress in another room!',
              });
            });
          }
        }, 2000);
        break;
      }
    }
  }

  /**
   * Handle media stream changes from mirror peers
   */
  private onMediaStreamChange = (mirrorId: string, audio?: MediaStream, video?: MediaStream): void => {
    const audioId = uuidv4();
    const videoId = uuidv4();

    this.mirrorMediaStreams[mirrorId] = { audio, video, audioId, videoId };
    this.mirrorMediaStreamSource.next(this.mirrorMediaStreams);
  };

  /**
   * Add the current user to the active presentation.
   * @returns boolean True if the user was added, false if the add was rejected.
   */
  public async joinPresentation(): Promise<boolean> {
    if (!this.isPresentationActive) {
      this.error('joinPresentation: Cannot join presentation, presentation is not active', {
        presenters: this.presenterIds,
      });
      return false;
    }

    if (this.presenterIds.length >= MAX_PRESENTERS) {
      this.error('joinPresentation: Cannot join presentation, presentation is at max capacity', {
        presenters: this.presenterIds,
      });
      return false;
    }

    const { isCrossRoomPresentation, isGuest, socialId, userId } = this;

    if (!this.user) {
      this.error('Attempted to join presentation with no user');
      return false;
    }

    this.info('Joining presentation', prettyPlayerId(this.user));
    const { firstName, lastName } = this.user;

    try {
      void this.multimediaUserService.handleRequestEndPushToTalk();
      void this.multimediaUserService.handleRequestLowerHand();
      await SocialStateMachine.shared.requestAddActivePresenter({
        userId,
        presenter: { userId, firstName, lastName, isGuest, socialId },
        updateType: isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      });
    } catch (e) {
      this.error('joinPresentation: Failed to join presentation:', e);
      return false;
    }

    // Set video quality to 1080p if presentation is being recorded or meeting wide recording is enabled
    if (this.isWideRecording || this.presentationRecorderService.isRecording()) {
      this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: PresenterVideoQuality.QUALITY_1080P }));
    }

    return true;
  }

  /**
   * Remove a user from the active presentation.
   * @param presenterID The presenter to remove.
   * @returns boolean True if the user was removed, false if the remove was rejected.
   */
  public async removeActivePresenter(presenterId: string, crossRoomPresentation?: UpdateType): Promise<boolean> {
    if (!this.isPresentationActive) {
      this.info('removeActivePresenter: Cannot remove presenter, presentation is not active', { presenterId });
      return false;
    }

    if (!this.presenterIds.includes(presenterId)) {
      this.error('removeActivePresenter: Cannot remove presenter from presentation, presenter not in presentation', {
        presenterId,
        presenters: this.presenterIds,
      });
      return false;
    }

    const { isCrossRoomPresentation, userId } = this;

    this.info('Removing active presenter', { presenterId, isCrossRoomPresentation });

    if (this.presenterIds.length === 1) {
      // clean up invitesToAdd
      this.requestedPresenterIds.forEach(async (requestedPresenterId) => {
        await this.removeInvitedPresenter(requestedPresenterId);
      });
      await this.endPresentation();
    } else {
      this.performerService.lastDismissedSpeaker = presenterId;
      await SocialStateMachine.shared.requestRemoveActivePresenter({
        presenterId,
        userId,
        updateType: crossRoomPresentation
          ? crossRoomPresentation
          : isCrossRoomPresentation
            ? UpdateType.ALL
            : UpdateType.CURRENT,
      });
    }
    return true;
  }

  /**
   * Remove an invited presenter from invited presenters, without adding them to active presenters.
   * @param presenterID The invited presenter to remove.
   * @returns boolean True if the user was removed, false if the remove was rejected.
   */
  public async removeInvitedPresenter(presenterId: string): Promise<boolean> {
    if (!this.isPresentationActive) {
      this.error('removeActivePresenter: Cannot remove invited presenter, presentation is not active', {
        presenterId,
      });
      return false;
    }

    if (!this.requestedPresenterIds.includes(presenterId)) {
      this.error(
        'removeActivePresenter: Cannot remove invited presenter from presentation, presenter not in presentation',
        {
          presenterId,
          presenters: this.presenterIds,
        },
      );
      return false;
    }

    this.info('Removing invited presenter', { presenterId });
    if (this.requestedPresenterIds.length === 1 && this.presenterIds.length < 1) {
      await this.endPresentation();
    } else {
      await SocialStateMachine.shared.requestRejectPresenterInvite({
        presenter: { userId: presenterId },
        userId: this.userId,
      });
    }

    return true;
  }

  /**
   *  End the current presentation
   * @param updateType - Type to be used in case the presentation is cross-room
   * @returns boolean True if the presentation was ended, false if the end was rejected.
   */
  public async endPresentation(updateType: UpdateType = UpdateType.ALL): Promise<boolean> {
    const { recordingId } = this.presentationRecorderService;
    if (!this.isPresentationActive) {
      this.error('endPresentation: Cannot end presentation, no presentation is active');
      return false;
    }

    // If we're ending a cross-room this presentation (non-OTHERS update type), restore the mix state to in-room.
    // For updating presentations in-place, the presentation modification process will update the mix type
    if (this.isCrossRoomPresentation) {
      this.presentationAudioService.disableCrossRoomMix();
    }

    // Unpublish livestreams from from cross-room but don't unpublish
    const preserveLivestream = updateType !== UpdateType.ALL;
    Object.values(this.presenters)
      .filter((p) => p.presenterType === PresenterType.LIVE_STREAM)
      .forEach(async (ls) => {
        await this.livestreamService.removeActiveLivestream(ls, preserveLivestream).catch((e) => this.error(e));
      });

    if (recordingId) {
      await this.presentationRecorderService.endRecording().catch((e) => {
        // This is not fatal to ending the presentation, so continue
        this.warn('endPresentation: Failed to end recording', e);
      });
    }

    try {
      await SocialStateMachine.shared.requestEndPresentation({
        userId: this.userId,
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      });
      await SocialStateMachine.shared.requestRemoveActivePresentation({
        socialId: this.socialId ?? '',
      });
    } catch (e) {
      this.error('endPresentation: Failed to end presentation', e);
      return false;
    }

    // returns background to full opacity
    this.transitionService.enableFadeInBackground().catch((err) => {
      this.error('Failed to execute transition', err);
    });

    return true;
  }

  /**
   *  End the current user's presentation
   * @returns boolean True if the presentation was ended, false if the end was rejected.
   */
  public async stopPresenting(): Promise<boolean> {
    if (this.shareScreenService.sharingInProgress) {
      await this.shareScreenService.stopSharing();
    }
    return this.removeActivePresenter(this.userId);
  }

  // #region Test Utilities

  /**
   * Simulate peer close
   * @param userId Close the peer for this userId
   */
  public closeSubscriberPeer(type: SubscriptionType.MIX | SubscriptionType.MIRROR, userId: string): void {
    let subscriber;
    switch (type) {
      case SubscriptionType.MIRROR:
        subscriber = this.mirrorSubscribers.get(userId);
        break;
      case SubscriptionType.MIX:
        subscriber = this.presentationAudioService.subscriber;
        break;
    }
    if (!subscriber) {
      this.warn('Subscriber peer not found', userId);
      return;
    }
    subscriber.forceClosePeer();
  }

  // #endregion
}

export enum MediaEmbedType {
  VIMEO = 'vimeo',
  YOUTUBE = 'youtube',
  TWITCH = 'twitch',
  SOUNDCLOUD = 'soundcloud',
  SPOTIFY = 'spotify',
}
